/**
 * DesignerTool — base class every Scene Designer tool subclasses.
 *
 * Phase 13 P0 (June 2026)
 * =======================
 *
 * Design intent
 * -------------
 *
 * The Scene Designer Toolbox framework lets us register a single,
 * canonical implementation of a tool ("MCQ editor", "telestration
 * toolbar", "metadata form") and then bind it to scenes data-
 * drivenly via predicates over the live catalog row + scene's
 * domain_metadata facets. Tools never know what scene type they're
 * running against; they just declare what they need (capabilities,
 * format, subject, domain) and the framework decides where they
 * mount.
 *
 * That decoupling has three direct consequences:
 *
 *   1. Adding a new scene type does NOT require touching tool code.
 *      The catalog row's capability list is the binding contract.
 *   2. Adding a new tool does NOT require touching scene-type code.
 *      The tool's `applies_to(ctx)` predicate decides reachability.
 *   3. Domain packs can register their own tools without mutating
 *      the platform. They share the same registry; binding rules
 *      stay the same.
 *
 * The base class itself is intentionally tiny — it's just a
 * validating container around a "spec" object. The validation runs
 * at registration time and again at mount time, so a tool that
 * silently lost its `Component` (e.g. via a bad import) fails loudly
 * at boot rather than turning into a missing-toolbar bug at runtime.
 *
 * @see DesignerToolRegistry — the singleton that holds registered tools
 * @see predicates           — composable predicate helpers
 * @see DesignerShell        — renders tools by slot in a canonical layout
 * @see DesignerContext      — shared state every tool's Component reads via useDesigner()
 */

/**
 * @typedef {('TOP_TOOLBAR'|'LEFT_RAIL'|'CANVAS_OVERLAY'|'RIGHT_INSP'|'BOTTOM_STRIP'|'MODAL_MOUNT')} DesignerSlot
 *
 * The six canonical slots a tool can mount into. Layout is fixed
 * by `DesignerShell` so the UX is identical across scene types and
 * domains. New slot ids are NOT permitted — adding one would
 * fragment the contract every existing tool depends on.
 */
export const DESIGNER_SLOTS = Object.freeze([
  'TOP_TOOLBAR',
  'LEFT_RAIL',
  'CANVAS_OVERLAY',
  'RIGHT_INSP',
  'BOTTOM_STRIP',
  'MODAL_MOUNT',
]);

const DESIGNER_SLOT_SET = new Set(DESIGNER_SLOTS);

/**
 * @typedef {Object} DesignerCategory
 *
 * Grouping label used when rendering the slot's tool list. Stable
 * strings; new categories can be added freely. Examples: "metadata",
 * "navigation", "annotation", "timeline", "facets".
 */

/**
 * @typedef {Object} DesignerToolSpec
 * @property {string} id              Globally unique tool id.
 *                                    Convention: `<scope>.<name>`.
 *                                    Scope MUST be `platform`,
 *                                    `domain.<domain_id>`, or
 *                                    `app.<surface>` (e.g.
 *                                    `app.platform_admin`).
 *                                    Examples:
 *                                      `platform.metadata_form`
 *                                      `domain.football.route_author`
 * @property {string} label           Human-readable name (i18n key
 *                                    reference allowed; the registry
 *                                    is content-agnostic).
 * @property {string=} icon           Lucide / heroicons name or an
 *                                    inline SVG ref. Optional.
 * @property {DesignerSlot} slot      Which slot this tool mounts into.
 * @property {string=} category       Grouping label inside the slot.
 *                                    The shell renders tools by
 *                                    category for readability.
 * @property {(ctx: DesignerContextValue) => boolean} applies_to
 *                                    Predicate consulted by the
 *                                    registry's `listFor()`. Must be
 *                                    pure and synchronous — no API
 *                                    calls, no setState, no I/O.
 *                                    Returns truthy to mount the tool.
 * @property {React.ComponentType=} Component
 *                                    The React component the shell
 *                                    mounts. Receives no props by
 *                                    default; reads everything via
 *                                    `useDesigner()`. Optional only
 *                                    for "header"/"divider" pseudo-
 *                                    tools that render via the
 *                                    framework itself; concrete tools
 *                                    MUST provide one.
 * @property {string=} helpText       Tooltip body. Optional.
 * @property {string=} keybinding     Keyboard shortcut string parsed
 *                                    by the framework's hotkey
 *                                    layer (e.g. `mod+s`, `shift+t`).
 *                                    Optional.
 * @property {number=} weight         Sort order within the slot. Lower
 *                                    weights render first. Default
 *                                    100. Use buckets: 0–49 system
 *                                    chrome, 50–99 platform tools,
 *                                    100–199 domain tools, 200+
 *                                    experimental.
 * @property {string=} feature_flag   Optional feature flag id. When
 *                                    set, the tool is hidden unless
 *                                    `isFlagOn(feature_flag)` returns
 *                                    truthy. Lets new tools roll out
 *                                    behind a flag without touching
 *                                    the registry's binding.
 * @property {string[]=} rbac         Optional list of role identifiers
 *                                    that may use the tool. Empty
 *                                    (default) = every coach. Resolved
 *                                    against the learner-app session's
 *                                    role claims; enforced by
 *                                    `DesignerToolRegistry.listFor()`
 *                                    and re-checked by `<ToolMount>`.
 *                                    Examples: `['platform_admin']`,
 *                                    `['coach','platform_admin']`.
 */

/**
 * Validate a spec object before constructing a DesignerTool. Throws
 * on any contract violation so misconfigured tools fail at boot,
 * not at coach-paint time.
 *
 * @param {DesignerToolSpec} spec
 * @returns {DesignerToolSpec} the same spec (allows chaining)
 */
function validateSpec(spec) {
  if (!spec || typeof spec !== 'object') {
    throw new Error('DesignerTool: spec must be an object');
  }

  const { id, label, slot, applies_to: appliesTo } = spec;

  if (!id || typeof id !== 'string') {
    throw new Error('DesignerTool: spec.id must be a non-empty string');
  }
  if (!/^(platform|domain\.[a-z0-9_]+|app\.[a-z0-9_]+)\.[a-z0-9_.]+$/.test(id)) {
    throw new Error(
      `DesignerTool[${id}]: id must match \`<scope>.<name>\` where ` +
      'scope is `platform`, `domain.<domain_id>`, or `app.<surface>`',
    );
  }

  if (!label || typeof label !== 'string') {
    throw new Error(`DesignerTool[${id}]: spec.label must be a non-empty string`);
  }

  if (!DESIGNER_SLOT_SET.has(slot)) {
    throw new Error(
      `DesignerTool[${id}]: spec.slot=${JSON.stringify(slot)} is not one of ` +
      `${JSON.stringify([...DESIGNER_SLOTS])}`,
    );
  }

  if (typeof appliesTo !== 'function') {
    throw new Error(`DesignerTool[${id}]: spec.applies_to must be a function`);
  }

  if (
    spec.Component !== undefined &&
    typeof spec.Component !== 'function' &&
    // React.memo / forwardRef return objects with a $$typeof tag.
    !(spec.Component && typeof spec.Component === 'object' && spec.Component.$$typeof)
  ) {
    throw new Error(
      `DesignerTool[${id}]: spec.Component must be a React component ` +
      '(function, forwardRef, or memo).',
    );
  }

  if (spec.rbac !== undefined) {
    if (!Array.isArray(spec.rbac) || spec.rbac.some((r) => typeof r !== 'string')) {
      throw new Error(`DesignerTool[${id}]: spec.rbac must be an array of role strings`);
    }
  }

  if (spec.weight !== undefined && typeof spec.weight !== 'number') {
    throw new Error(`DesignerTool[${id}]: spec.weight must be a number`);
  }

  return spec;
}

/**
 * The DesignerTool class. Concrete tools either subclass it or call
 * `new DesignerTool({...spec})` directly — both work. Subclassing is
 * useful when a family of tools share `applies_to` logic (e.g. the
 * Phase 13 P3 quiz-format pickers all bind on
 * `scene_type=interactive_quiz`).
 *
 * @example
 *   import { DesignerTool, DESIGNER_SLOTS } from './DesignerTool';
 *   import { requiresCapability } from './predicates';
 *   import MetadataForm from './tools/MetadataForm';
 *
 *   export default new DesignerTool({
 *     id:           'platform.metadata_form',
 *     label:        'Metadata',
 *     slot:         'TOP_TOOLBAR',
 *     category:     'metadata',
 *     applies_to:   () => true,                  // every scene
 *     Component:    MetadataForm,
 *     weight:       50,
 *     keybinding:   'mod+i',
 *   });
 */
export class DesignerTool {
  /**
   * @param {DesignerToolSpec} spec
   */
  constructor(spec) {
    const validated = validateSpec({
      // Defaults applied BEFORE validation so e.g. an explicit
      // `weight: 0` still wins over the default.
      weight: 100,
      helpText: '',
      keybinding: null,
      feature_flag: null,
      rbac: [],
      ...spec,
    });

    Object.assign(this, validated);

    // Freeze the static metadata so registered tools can't be
    // mutated after-the-fact (a class of bugs we hit when an old
    // designer page tweaked tool config in a useEffect).
    Object.freeze(this);
  }

  /**
   * Stable identity for React keys + registry lookups.
   * @returns {string}
   */
  toString() {
    return `DesignerTool(${this.id})`;
  }

  /**
   * Test-only: re-run the spec validator. Lets us assert in unit
   * tests that a malformed spec throws at construction without
   * exporting `validateSpec` on the public surface.
   * @internal
   */
  static _validateSpecForTests(spec) {
    return validateSpec(spec);
  }
}

export default DesignerTool;
